import { Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService, GetPermissionsAppComponent, LaunchDarklyFlag, LaunchDarklyService, PermissionsDevtoolbarService } from '@anduin/auth';
import { ConfigService } from '@anduin/config';
import { AssignedDraftBillsUpdated, BridgeDatasource, IconRegistryService, WebViewerPreloadService } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { Subject, combineLatest } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ROUTE_TOKEN_PASSWORDLESS_LOGIN, ROUTE_TOKEN_SUPPORT_LOGIN } from './routing-constants';
import { UsersnapService } from './services/usersnap.service';
import { ZendeskWidgetService } from './services/zendesk-widget.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  unsubscribe$: Subject<void> = new Subject<void>();

  bannerMessage = toSignal(this.configService.configsLoaded$.pipe(map(_ => this.configService.bannerMessage)), { initialValue: undefined });
  bannerTheme = toSignal(this.configService.configsLoaded$.pipe(map(_ => this.configService.bannerTheme)), { initialValue: 'WARN' });
  bannerDismissed = signal(false);

  showDevToolbar = computed(() => this.configService.enableDeveloperToolbar);

  toolbarConfig = computed(() => ({
    showFeatureFlagsTool: true,
    showAppFeaturesTool: true,
    showPermissionsTool: true,
    showPresetsTool: false,
    enabled: this.showDevToolbar(),
  }));

  isStaffDisabled = signal(false);
  showSideNav = computed(() => !this.isStaffDisabled());

  // Inject service to trigger its constructor and initialize dev toolbar
  private readonly permissionsDevtoolbarService = inject(PermissionsDevtoolbarService);

  private readonly _bridgeDateFilterUtcFixEnabled = inject(LaunchDarklyService).getFeatureFlagSignal(
    LaunchDarklyFlag.BRIDGE_DATASOURCE_DATE_FILTER_UTC_FIX,
  );

  authenticateUserBeforeEnablingRouting$ = this.authService.logInSSOUser().pipe(
    take(1),
    tap((isAuthenticated) => {
      // routing should not occur when an unauthenticated user tries to access any route other than logout or error
      if (isAuthenticated || window.location.href.includes('logout') || window.location.href.includes('error')) {
        this.router.initialNavigation();
      }

      // Wait until logged in to determine if staff is disabled
      this.isStaffDisabled.set(this.authService.isStaffDisabled());

      // The chatbot mint needs a session, so the widget starts only once auth resolves.
      if (isAuthenticated) {
        this.zendeskWidgetService.initialize();
      }
    }),
    // Only dispatch actions when the user is authenticated
    filter<boolean>(Boolean),
    switchMap((_) => this.store.dispatch([new GetPermissionsAppComponent(), new AssignedDraftBillsUpdated()])),
    takeUntil(this.unsubscribe$),
  );

  private readonly _webViewerPreloadService = inject(WebViewerPreloadService);

  constructor(
    private store: Store,
    private authService: AuthService,
    private iconRegistry: IconRegistryService,
    private router: Router,
    private readonly usersnapService: UsersnapService,
    private readonly configService: ConfigService,
    private readonly zendeskWidgetService: ZendeskWidgetService,
  ) {
    this.iconRegistry.registerIcons();

    // FIRM-5012: keep BridgeDatasource's date-filter UTC fix toggle in sync with LaunchDarkly so the
    // payment history / invoices / AR / email-delivery-hub date filters can be flipped per-firm without
    // rebuilding. The dev toolbar toggle takes effect live via the reactive signal.
    effect(() => {
      BridgeDatasource.useUtcOffsetDateFilterFix = Boolean(this._bridgeDateFilterUtcFixEnabled());
    });
  }

  private shouldBypassAuth(): boolean {
    return window.location.href.includes(ROUTE_TOKEN_PASSWORDLESS_LOGIN) || window.location.href.includes(ROUTE_TOKEN_SUPPORT_LOGIN);
  }

  ngOnInit(): void {
    if (this.configService.enableFirmPortalUsersnapPlugin) {
      this.usersnapService.initialize();
    }

    if (this.shouldBypassAuth()) {
      this.router.initialNavigation();
    } else {
      this.authenticateUserBeforeEnablingRouting$.subscribe();
    }

    this.schedulePreviewPreload();
  }

  /**
   * Warm up the Apryse WebViewer instance off-screen at idle so the first
   * file preview opens instantly.
   */
  private schedulePreviewPreload(): void {
    this.getIdleCallback()(() => this._webViewerPreloadService.preload());
  }

  /**
   * Returns a scheduler for running work when the browser is idle.
   *
   * Safari does not support `requestIdleCallback`, so we fall back to
   * `setTimeout` with a minimal delay to defer the work to the next tick.
   */
  private getIdleCallback(): (cb: () => void) => number {
    return (
      (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback ??
      ((cb: () => void) => window.setTimeout(cb, 1))
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
